const ABSOLUTE_HTTP_URL_REGEX = /(https?:\/\/[^\s"'<>]+)/gi;
const DOWNLOAD_FILE_NAME_REGEX = /\.(zip|pdf|csv|xlsx?|docx?|pptx?|txt)(?:$|[?#])/i;
const SAS_QUERY_KEY_REGEX = /(?:^|[?&])(sv|sig|se|sp|sr)=/i;

export const toComparableValue = (value) =>
  value === null || typeof value === 'undefined' ? '' : String(value).trim().toLowerCase();

export const tryParseJsonString = (value) => {
  if (typeof value !== 'string') return null;
  const trimmedValue = value.trim();
  if (!trimmedValue || !['{', '['].includes(trimmedValue[0])) return null;
  try {
    return JSON.parse(trimmedValue);
  } catch {
    return null;
  }
};

export const sanitizeLink = (value = '') => value.trim().replace(/[),.;]+$/g, '');

export const isLikelyDownloadLink = (value = '') => {
  if (typeof value !== 'string' || !value.trim()) return false;

  try {
    const parsedUrl = new URL(value);
    return DOWNLOAD_FILE_NAME_REGEX.test(parsedUrl.pathname) || SAS_QUERY_KEY_REGEX.test(parsedUrl.search);
  } catch {
    return false;
  }
};

export const getNotificationTimestamp = (notification) => {
  const timestampCandidates = [
    notification?.createdBy?.date,
    notification?.created,
    notification?.createdAt,
    notification?.date,
    notification?.timestamp,
    notification?.updatedAt,
  ];

  for (const timestampCandidate of timestampCandidates) {
    if (!timestampCandidate) continue;
    const parsedTimestamp = new Date(timestampCandidate).getTime();
    if (!Number.isNaN(parsedTimestamp)) return parsedTimestamp;
  }

  return null;
};

export const getLatestNotification = (notifications = []) => {
  return notifications.reduce((latestNotification, currentNotification) => {
    if (!latestNotification) return currentNotification;

    const latestTimestamp = getNotificationTimestamp(latestNotification);
    const currentTimestamp = getNotificationTimestamp(currentNotification);

    if (latestTimestamp === null && currentTimestamp === null) return currentNotification;
    if (latestTimestamp === null) return currentNotification;
    if (currentTimestamp === null) return latestNotification;
    return currentTimestamp >= latestTimestamp ? currentNotification : latestNotification;
  }, null);
};

const collectJobCandidatesFromValue = (value, candidates, visited = new WeakSet()) => {
  if (value === null || typeof value === 'undefined') return;

  if (typeof value === 'string') {
    const parsedStringValue = tryParseJsonString(value);
    if (parsedStringValue) {
      collectJobCandidatesFromValue(parsedStringValue, candidates, visited);
      return;
    }
    candidates.push(value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectJobCandidatesFromValue(item, candidates, visited));
    return;
  }

  if (typeof value === 'object') {
    if (visited.has(value)) return;
    visited.add(value);

    candidates.push(
      value.jobId,
      value.jobID,
      value.JobId,
      value.additionalId,
      value.entityId,
      value.id,
    );

    Object.values(value).forEach((item) => collectJobCandidatesFromValue(item, candidates, visited));
  }
};

export const getNotificationJobCandidates = (notification) => {
  const candidates = [];
  collectJobCandidatesFromValue(notification, candidates);
  return candidates
    .map((value) => toComparableValue(value))
    .filter(Boolean);
};

export const doesNotificationMatchJobId = (notification, jobId) => {
  const comparableJobId = toComparableValue(jobId);
  if (!comparableJobId) return false;

  return getNotificationJobCandidates(notification).some(
    (candidate) => candidate === comparableJobId || candidate.includes(comparableJobId),
  );
};

const collectLinksFromValue = (value, links, visited = new WeakSet()) => {
  if (value === null || typeof value === 'undefined') return;

  if (typeof value === 'string') {
    const parsedStringValue = tryParseJsonString(value);
    if (parsedStringValue) {
      collectLinksFromValue(parsedStringValue, links, visited);
      return;
    }

    const directUrlMatch = value.trim().match(/^https?:\/\/[^\s"'<>]+$/i);
    if (directUrlMatch?.[0]) {
      links.push(sanitizeLink(directUrlMatch[0]));
      return;
    }

    const embeddedUrlMatches = value.match(ABSOLUTE_HTTP_URL_REGEX);
    if (embeddedUrlMatches?.length) {
      embeddedUrlMatches.forEach((match) => links.push(sanitizeLink(match)));
    }
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectLinksFromValue(item, links, visited));
    return;
  }

  if (typeof value === 'object') {
    if (visited.has(value)) return;
    visited.add(value);

    links.push(
      value.link,
      value.url,
      value.downloadLink,
      value.downloadUrl,
      value.fileLink,
      value.fileUrl,
    );

    Object.values(value).forEach((item) => collectLinksFromValue(item, links, visited));
  }
};

export const getNotificationDownloadLink = (notification) => {
  const links = [];
  collectLinksFromValue(notification, links);

  const normalizedLinks = links
    .filter((value) => typeof value === 'string' && value.trim())
    .map((value) => sanitizeLink(value))
    .filter((value) => /^https?:\/\//i.test(value));

  const likelyDownloadLink = normalizedLinks.find((link) => isLikelyDownloadLink(link));
  if (likelyDownloadLink) {
    return likelyDownloadLink;
  }

  return normalizedLinks[0] || '';
};

export const getFileNameFromLink = (link, fallbackName) => {
  try {
    const parsedUrl = new URL(link);
    const fileName = parsedUrl.pathname.split('/').filter(Boolean).pop();
    return fileName ? decodeURIComponent(fileName) : fallbackName;
  } catch {
    return fallbackName;
  }
};

